/** @jsxImportSource @emotion/react */
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/react'
import { Popover } from 'antd'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core'
import colors from '@sportninja/common/constants/appColors'
import CalendarGameCard, { CalendarGame } from './CalendarGameCard'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import {
  getTimeRangesForFacilityAndDay,
  splitIntoHourSlots,
  computeConstraintBlockedDates,
  formatLocalDate,
} from '../utils/gameEditHelpers'
import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'

export interface CalendarDay {
  date: Date
  dayNumber: number
  isCurrentMonth: boolean
  isBlackout: boolean
  isToday: boolean
  games: CalendarGame[]
}

interface CalendarGridProps {
  year: number
  month: number // 0-indexed (0 = January)
  games: CalendarGame[]
  blackoutDates: Date[]
  onGameClick?: (game: CalendarGame) => void
  onDayClick?: (date: Date) => void
  // Popover props
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
  // Drag-and-drop props
  onDragEnd?: (gameId: string, targetDate: Date) => void
  timeSlots?: TimeSlot[]
  // Competition date boundaries
  competitionStartDate?: string // YYYY-MM-DD
  competitionEndDate?: string // YYYY-MM-DD
  // Scheduler constraints for feasibility hints
  constraints?: SchedulerConstraint[]
}

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

// ─── Draggable Game Card Wrapper ────────────────────────────────

interface DraggableGameCardProps {
  game: CalendarGame
  onGameClick?: (game: CalendarGame) => void
  isPopoverOpen: boolean
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
}

const DraggableGameCard: React.FC<DraggableGameCardProps> = ({
  game,
  onGameClick,
  isPopoverOpen,
  popoverContent,
  onPopoverOpenChange,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `game-${game.id}`,
    data: { game },
  })

  const handleClick = useCallback(
    (clickedGame: CalendarGame) => {
      onGameClick?.(clickedGame)
    },
    [onGameClick]
  )

  const cardContent = (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      css={css`
        opacity: ${isDragging ? 0.4 : 1};
        touch-action: none;
        cursor: pointer;
      `}
    >
      <CalendarGameCard game={game} onClick={handleClick} />
    </div>
  )

  if (popoverContent && onPopoverOpenChange) {
    return (
      <Popover
        content={popoverContent}
        trigger='click'
        open={isPopoverOpen}
        onOpenChange={(visible) => {
          if (!visible) {
            onPopoverOpenChange(null)
          }
        }}
        placement='rightTop'
        // eslint-disable-next-line react-native/no-inline-styles
        overlayStyle={{ zIndex: 1050 }}
        // eslint-disable-next-line react-native/no-inline-styles
        overlayInnerStyle={{
          background: colors.SECONDARY_300,
          border: `1px solid ${colors.SECONDARY_100}`,
          borderRadius: '8px',
          padding: 0,
        }}
        destroyTooltipOnHide
      >
        {cardContent}
      </Popover>
    )
  }

  return cardContent
}

// ─── Droppable Day Cell Wrapper ─────────────────────────────────

interface DroppableDayCellProps {
  day: CalendarDay
  index: number
  dayCellStyles: ReturnType<typeof css>
  dayNumberStyles: ReturnType<typeof css>
  gamesContainerStyles: ReturnType<typeof css>
  blackoutLabelStyles: ReturnType<typeof css>
  onDayClick?: (date: Date) => void
  onGameClick?: (game: CalendarGame) => void
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
  dndEnabled: boolean
  isDragging: boolean
  isFeasible: boolean
}

const MAX_VISIBLE_GAMES = 3

const DroppableDayCell: React.FC<DroppableDayCellProps> = ({
  day,
  index,
  dayCellStyles: dayCellCss,
  dayNumberStyles: dayNumberCss,
  gamesContainerStyles: gamesContainerCss,
  blackoutLabelStyles: blackoutLabelCss,
  onDayClick,
  onGameClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
  dndEnabled,
  isDragging,
  isFeasible,
}) => {
  const dateISO = day.date.toISOString().slice(0, 10)
  const [expandedOpen, setExpandedOpen] = useState(false)
  const expandedRef = useRef<HTMLDivElement>(null)

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateISO}`,
    data: { date: day.date, isBlackout: day.isBlackout },
    disabled: !dndEnabled || day.isBlackout || !isFeasible,
  })

  // Close expanded popover on outside click
  useEffect(() => {
    if (!expandedOpen) {
      return
    }
    const handleClick = (e: MouseEvent) => {
      if (
        expandedRef.current &&
        !expandedRef.current.contains(e.target as Node)
      ) {
        setExpandedOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [expandedOpen])

  // Determine highlight style during drag
  const dragHighlight =
    isDragging && day.isCurrentMonth && !day.isBlackout
      ? isFeasible
        ? css`
            background: ${colors.SUCCESS_300}12;
            outline: 1px solid ${colors.SUCCESS_300}40;
            outline-offset: -1px;
          `
        : css`
            opacity: 0.35;
          `
      : undefined

  const hoverHighlight =
    isOver && !day.isBlackout
      ? css`
          outline: 2px dashed ${colors.PRIMARY};
          outline-offset: -2px;
        `
      : undefined

  const totalGames = day.games.length
  const visibleGames = day.games.slice(0, MAX_VISIBLE_GAMES)
  const hiddenCount = totalGames - MAX_VISIBLE_GAMES

  const dayLabel = day.date
    .toLocaleDateString('en-US', { weekday: 'short' })
    .toUpperCase()

  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })

  // Position the portal popover relative to the "+N more" button
  useEffect(() => {
    if (!expandedOpen || !moreButtonRef.current) {
      return
    }
    const rect = moreButtonRef.current.getBoundingClientRect()
    setPopoverPos({
      top: rect.top,
      left: rect.left + rect.width / 2,
    })
  }, [expandedOpen])

  return (
    <div
      ref={setNodeRef}
      key={`${day.date.toISOString()}-${index}`}
      css={[dayCellCss, dragHighlight, hoverHighlight]}
      onClick={() => onDayClick?.(day.date)}
    >
      <span css={dayNumberCss}>{day.dayNumber}</span>

      {day.isBlackout && day.isCurrentMonth && (
        <span css={blackoutLabelCss}>BLACKOUT DAY</span>
      )}

      {!day.isBlackout && totalGames > 0 && (
        <>
          <div css={gamesContainerCss}>
            {visibleGames.map((game) => (
              <DraggableGameCard
                key={game.id}
                game={game}
                onGameClick={onGameClick}
                isPopoverOpen={popoverGameId === game.id}
                popoverContent={
                  popoverGameId === game.id ? popoverContent : undefined
                }
                onPopoverOpenChange={onPopoverOpenChange}
              />
            ))}
          </div>
          {hiddenCount > 0 && (
            <button
              ref={moreButtonRef}
              type='button'
              css={moreButtonStyles}
              onClick={(e) => {
                e.stopPropagation()
                setExpandedOpen((prev) => !prev)
              }}
            >
              {hiddenCount} more
            </button>
          )}
          {expandedOpen &&
            createPortal(
              <div
                ref={expandedRef}
                css={expandedPopoverStyles}
                // eslint-disable-next-line react-native/no-inline-styles
                style={{
                  top: popoverPos.top,
                  left: popoverPos.left,
                  transform: 'translate(-50%, -100%)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div css={expandedPopoverHeaderStyles}>
                  <div>
                    <span css={expandedPopoverDayLabelStyles}>{dayLabel}</span>
                    <span css={expandedPopoverDayNumberStyles}>
                      {day.dayNumber}
                    </span>
                  </div>
                  <button
                    type='button'
                    css={expandedCloseButtonStyles}
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpandedOpen(false)
                    }}
                    aria-label='Close'
                  >
                    &times;
                  </button>
                </div>
                <div css={expandedPopoverListStyles}>
                  {day.games.map((game) => (
                    <DraggableGameCard
                      key={game.id}
                      game={game}
                      onGameClick={onGameClick}
                      isPopoverOpen={popoverGameId === game.id}
                      popoverContent={
                        popoverGameId === game.id ? popoverContent : undefined
                      }
                      onPopoverOpenChange={onPopoverOpenChange}
                    />
                  ))}
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  )
}

// ─── Expanded Day Popover Styles ──────────────────────────────

const moreButtonStyles = css`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  text-align: left;
  flex-shrink: 0;

  &:hover {
    background: ${colors.SECONDARY_400};
  }
`

const expandedPopoverStyles = css`
  position: fixed;
  z-index: 1050;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  padding: 12px;
  min-width: 220px;
  max-width: 280px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`

const expandedPopoverHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const expandedPopoverDayLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
`

const expandedPopoverDayNumberStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.WHITE};
`

const expandedCloseButtonStyles = css`
  background: transparent;
  border: 1px solid ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_70};
  font-size: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    color: ${colors.WHITE};
    border-color: ${colors.NEUTRAL_70};
  }
`

const expandedPopoverListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.NEUTRAL_100};
    border-radius: 2px;
  }
`

/**
 * Calendar Grid Component
 * Displays a monthly calendar view with games, supporting inline edit popovers and DnD
 */
const CalendarGrid: React.FC<CalendarGridProps> = ({
  year,
  month,
  games,
  blackoutDates,
  onGameClick,
  onDayClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
  onDragEnd: onDragEndProp,
  timeSlots = [],
  competitionStartDate,
  competitionEndDate,
  constraints = [],
}) => {
  const [activeGame, setActiveGame] = React.useState<CalendarGame | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  // Generate calendar days for the month
  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = []
    const now = new Date()
    const todayYear = now.getFullYear()
    const todayMonth = now.getMonth()
    const todayDate = now.getDate()

    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const startDayOfWeek = firstDayOfMonth.getDay()
    const totalDaysInMonth = lastDayOfMonth.getDate()

    const checkToday = (d: Date) =>
      d.getFullYear() === todayYear &&
      d.getMonth() === todayMonth &&
      d.getDate() === todayDate

    // Get days from previous month to fill the first week
    const prevMonth = new Date(year, month, 0)
    const daysInPrevMonth = prevMonth.getDate()

    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const dayNumber = daysInPrevMonth - i
      const date = new Date(year, month - 1, dayNumber)
      days.push({
        date,
        dayNumber,
        isCurrentMonth: false,
        isToday: checkToday(date),
        isBlackout: isDateBlackout(date, blackoutDates),
        games: getGamesForDate(date, games),
      })
    }

    // Current month days
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const date = new Date(year, month, day)
      days.push({
        date,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: checkToday(date),
        isBlackout: isDateBlackout(date, blackoutDates),
        games: getGamesForDate(date, games),
      })
    }

    // Fill remaining days from next month
    const remainingDays = 42 - days.length // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day)
      days.push({
        date,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: checkToday(date),
        isBlackout: isDateBlackout(date, blackoutDates),
        games: getGamesForDate(date, games),
      })
    }

    return days
  }, [year, month, games, blackoutDates])

  const preventScroll = useCallback((e: Event) => {
    e.preventDefault()
  }, [])

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const game = event.active.data.current?.game as CalendarGame | undefined
      setActiveGame(game ?? null)
      // Prevent page scrolling while dragging (event-based only to avoid scroll jump)
      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    },
    [preventScroll]
  )

  const cleanupDragScroll = useCallback(() => {
    document.removeEventListener('wheel', preventScroll)
    document.removeEventListener('touchmove', preventScroll)
  }, [preventScroll])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveGame(null)
      cleanupDragScroll()
      const { active, over } = event
      if (!over || !onDragEndProp) {
        return
      }

      const game = active.data.current?.game as CalendarGame | undefined

      const overData = over.data.current
      const targetDate = overData?.date as Date | undefined
      if (!game || !targetDate) {
        return
      }

      onDragEndProp(game.id, targetDate)
    },
    [onDragEndProp, cleanupDragScroll]
  )

  const handleDragCancel = useCallback(() => {
    setActiveGame(null)
    cleanupDragScroll()
  }, [cleanupDragScroll])

  const dndEnabled = !!onDragEndProp

  // Parse competition date boundaries once for feasibility checks
  const compStart = useMemo(
    () =>
      competitionStartDate
        ? new Date(competitionStartDate + 'T00:00:00')
        : null,
    [competitionStartDate]
  )
  const compEnd = useMemo(
    () =>
      competitionEndDate ? new Date(competitionEndDate + 'T23:59:59') : null,
    [competitionEndDate]
  )

  // Feasible days-of-week based on the dragged game's facility time slots.
  // Only days where the game's Location + Surface has available hour slots
  // will be highlighted green.
  const feasibleDaysOfWeek = useMemo(() => {
    if (!activeGame) {
      return new Set<number>()
    }
    const facilityId = activeGame.surfaceId
    if (!facilityId) {
      return new Set<number>()
    }
    const divisionId = activeGame.divisionId || undefined
    const days = new Set<number>()
    for (let dow = 0; dow < 7; dow++) {
      const ranges = getTimeRangesForFacilityAndDay(
        timeSlots,
        facilityId,
        dow,
        divisionId
      )
      if (splitIntoHourSlots(ranges).length > 0) {
        days.add(dow)
      }
    }
    return days
  }, [activeGame, timeSlots])

  // Dates blocked by hard constraints (min_rest_days, max_games_per_team_per_day, team_bye_period)
  const constraintBlockedDates = useMemo(() => {
    if (!activeGame || constraints.length === 0) {
      return new Set<string>()
    }
    return computeConstraintBlockedDates(activeGame, games, constraints)
  }, [activeGame, games, constraints])

  // ─── Styles ─────────────────────────────────────────────────────

  const containerStyles = css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: calc(100vh - 200px);
    min-height: 600px;
    background: ${colors.SECONDARY_500};
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${colors.SECONDARY_200};
  `

  const headerRowStyles = css`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: ${colors.SECONDARY_400};
    border-bottom: 1px solid ${colors.SECONDARY_200};
  `

  const headerCellStyles = css`
    padding: 8px 12px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.5px;
    color: ${colors.NEUTRAL_70};
    text-align: center;
    text-transform: uppercase;
    border-right: 1px solid ${colors.SECONDARY_200};

    &:last-child {
      border-right: none;
    }
  `

  const gridStyles = css`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(6, 1fr);
    flex: 1;
    min-height: 0;
  `

  const dayCellStylesFn = (
    isCurrentMonth: boolean,
    isBlackout: boolean,
    isToday: boolean
  ) => css`
    padding: 8px;
    background: ${isBlackout
      ? colors.SECONDARY_600
      : isToday
      ? `${colors.PRIMARY}10`
      : isCurrentMonth
      ? colors.SECONDARY_500
      : colors.SECONDARY_600};
    border-right: 1px solid ${isToday ? colors.PRIMARY : colors.SECONDARY_200};
    border-bottom: 1px solid ${isToday ? colors.PRIMARY : colors.SECONDARY_200};
    ${isToday
      ? `border-left: 1px solid ${colors.PRIMARY}; border-top: 1px solid ${colors.PRIMARY};`
      : ''}
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    opacity: ${isCurrentMonth ? 1 : 0.5};
    overflow: hidden;
    min-height: 0;

    &:nth-of-type(7n) {
      border-right: ${isToday ? `1px solid ${colors.PRIMARY}` : 'none'};
    }
  `

  const dayNumberStylesFn = (isBlackout: boolean, isToday: boolean) => css`
    font-family: 'Inter', sans-serif;
    font-weight: ${isToday ? 700 : 500};
    font-size: 12px;
    line-height: ${isToday ? '22px' : '14px'};
    color: ${isBlackout
      ? colors.NEUTRAL_100
      : isToday
      ? colors.SECONDARY_500
      : colors.WHITE};
    margin: 0 0 4px 0;
    ${isToday
      ? `
      background: ${colors.PRIMARY};
      width: 22px;
      height: 22px;
      border-radius: 50%;
      text-align: center;
    `
      : ''}
  `

  const gamesContainerStyles = css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.NEUTRAL_100};
      border-radius: 2px;
    }
  `

  const blackoutLabelStyles = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 1px;
    color: ${colors.NEUTRAL_100};
    text-transform: uppercase;
    white-space: nowrap;
    z-index: 1;
  `

  const gridContent = (
    <div css={gridStyles}>
      {calendarDays.map((day, index) => (
        <DroppableDayCell
          key={`${day.date.toISOString()}-${index}`}
          day={day}
          index={index}
          dayCellStyles={dayCellStylesFn(
            day.isCurrentMonth,
            day.isBlackout,
            day.isToday
          )}
          dayNumberStyles={dayNumberStylesFn(day.isBlackout, day.isToday)}
          gamesContainerStyles={gamesContainerStyles}
          blackoutLabelStyles={blackoutLabelStyles}
          onDayClick={onDayClick}
          onGameClick={onGameClick}
          popoverGameId={popoverGameId}
          popoverContent={popoverContent}
          onPopoverOpenChange={onPopoverOpenChange}
          dndEnabled={dndEnabled}
          isDragging={!!activeGame}
          isFeasible={
            !day.isBlackout &&
            feasibleDaysOfWeek.has(day.date.getDay()) &&
            (!compStart || day.date >= compStart) &&
            (!compEnd || day.date <= compEnd) &&
            !constraintBlockedDates.has(formatLocalDate(day.date))
          }
        />
      ))}
    </div>
  )

  return (
    <div css={containerStyles}>
      {/* Header Row - Days of Week */}
      <div css={headerRowStyles}>
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} css={headerCellStyles}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid with DnD */}
      {dndEnabled ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {gridContent}
          <DragOverlay>
            {activeGame && (
              <div
                css={css`
                  opacity: 0.8;
                  pointer-events: none;
                `}
              >
                <CalendarGameCard game={activeGame} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        gridContent
      )}
    </div>
  )
}

// Helper function to check if a date is a blackout date
export function isDateBlackout(date: Date, blackoutDates: Date[]): boolean {
  return blackoutDates.some(
    (blackout) =>
      blackout.getFullYear() === date.getFullYear() &&
      blackout.getMonth() === date.getMonth() &&
      blackout.getDate() === date.getDate()
  )
}

// Helper function to get games for a specific date
export function getGamesForDate(
  date: Date,
  games: CalendarGame[]
): CalendarGame[] {
  return games.filter((game) => {
    const gameDate = new Date(game.date)
    return (
      gameDate.getFullYear() === date.getFullYear() &&
      gameDate.getMonth() === date.getMonth() &&
      gameDate.getDate() === date.getDate()
    )
  })
}

export default CalendarGrid
